import * as React from "react";
import { Compass, Sparkles, Zap, type LucideProps } from "lucide-react";
import { useEntitlements } from "../../hooks/useEntitlements";
import { UserMessageAction } from "../../interfaces/chatSession";
import { SentActionIndicator } from "./SentActionIndicator";

/**
 * Message sent by the Quick Analysis action.
 *
 * It names the review depth in plain words rather than relying on the request
 * flag alone, and that is deliberate. The flag is per-turn - it is what lets
 * the backend withhold tools and pre-load the playbook for the opening turn -
 * but it is not re-sent afterwards. What keeps every later answer in the same
 * shape is this sentence sitting in the conversation history, where the model
 * reads it exactly as it would read a user who typed the same thing.
 */
export const QUICK_ANALYSIS_PROMPT =
  "Run a Light review of this contract against my self-service playbook and give me the bottom line.";

/**
 * Label for the action, shared by the button and by the chat bubble that
 * stands in for its message, so the two can never drift apart. It names the
 * tier because pressing it asserts one: the user is telling us this contract
 * is Tier 3, and a bare "Quick Analysis" hides that behind a speed promise.
 */
export const QUICK_ANALYSIS_LABEL = "Tier 3 Contract Quick Analysis";

/**
 * Agents whose system prompt was written for this review.
 *
 * Only the Redline Analyzer has one. The backend serves the opening turn by
 * withholding every tool and pre-loading the playbook, and that only produces
 * the intended answer where the instructions explaining it are also present.
 * On any other agent the same click would strip the tools from a prompt that
 * still has its context-gathering step active and no format spec - a worse
 * answer than the ordinary flow gives, from a button the user took for a
 * shortcut.
 */
const TIER3_CAPABLE_AGENTS = ["red-line-analyzer"];

/**
 * Whether the Tier 3 workflow - the Quick Analysis action and the Draft
 * Certificate that follows it - can be offered on this agent.
 */
export function isTier3CapableAgent(agentId: string): boolean {
  return TIER3_CAPABLE_AGENTS.includes(agentId);
}

/** Accent used by both the action and the bubble that stands in for it. */
const QUICK_ANALYSIS_ACCENT = "#7c3aed";

/**
 * Stands in for the Quick Analysis message in the chat. The message itself is
 * still sent and still sits in the conversation history, because it is what
 * keeps later turns in Tier 3 shape.
 */
export function QuickAnalysisIndicator() {
  return (
    <SentActionIndicator Icon={Zap} label={QUICK_ANALYSIS_LABEL} accent={QUICK_ANALYSIS_ACCENT} />
  );
}

interface QuickStartButton {
  title: string;
  message: string;
  gradient: string;
  animatedGradient: string;
  textColor: string;
  iconColor: string;
  Icon: React.ComponentType<LucideProps>;
  /** Set when the message is sent by an add-in action rather than as typed
   *  text. Absent on actions that just prefill a prompt. */
  action?: UserMessageAction;
}

interface QuickStartButtonsProps {
  onPromptSelect: (message: string, opts?: { action: UserMessageAction }) => void;
  isVisible: boolean;
  /** Currently selected agent; decides whether Quick Analysis is offered. */
  agentId: string;
}

const QuickStartButtons: React.FC<QuickStartButtonsProps> = ({
  onPromptSelect,
  isVisible,
  agentId,
}) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const { tier3Review: isTier3ReviewEnabled } = useEntitlements();
  const showQuickAnalysis = isTier3ReviewEnabled && isTier3CapableAgent(agentId);

  const buttons: QuickStartButton[] = [
    {
      title: "Where should I start?",
      message:
        "I would appreciate some guidance on where to start and what to focus on first. A prompt coach would be very helpful here.",
      gradient: "transparent",
      animatedGradient: "linear-gradient(90deg, #0066cc, #75B6FF, #0066cc, #75B6FF)",
      textColor: "#6b7280",
      iconColor: "#9ca3af",
      Icon: Compass,
    },
    // Quick Analysis replaces the former "Get Inspiration" action, and only
    // where a Tier 3 review can actually be served: an entitled tenant on an
    // agent that carries the protocol. Everyone else keeps what they had.
    showQuickAnalysis
      ? {
          title: QUICK_ANALYSIS_LABEL,
          message: QUICK_ANALYSIS_PROMPT,
          action: "quick_analysis",
          gradient: "transparent",
          animatedGradient: "linear-gradient(90deg, #8b5cf6, #c4b5fd, #8b5cf6, #c4b5fd)",
          textColor: "#6b7280",
          iconColor: "#9ca3af",
          Icon: Zap,
        }
      : {
          title: "Get Inspiration",
          message:
            "I need some inspiration on what to do. Analyze the context and give me the top 3 ideas. An action-oriented Prompt would be very useful here.",
          gradient: "transparent",
          animatedGradient: "linear-gradient(90deg, #8b5cf6, #c4b5fd, #8b5cf6, #c4b5fd)",
          textColor: "#6b7280",
          iconColor: "#9ca3af",
          Icon: Sparkles,
        },
  ];

  const handleClick = (button: QuickStartButton) => {
    onPromptSelect(button.message, button.action ? { action: button.action } : undefined);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        // The actions keep their text on one line, so without wrapping they are
        // clipped rather than shortened once their combined width passes the
        // pane's. The pane is commonly left at its default narrow width.
        flexWrap: "wrap",
        gap: "8px",
        marginTop: "18px",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(-6px)",
        transition: "opacity 250ms ease-out, transform 250ms ease-out",
        pointerEvents: isVisible ? "auto" : "none",
      }}
    >
      {buttons.map((button, index) => (
        <button
          key={index}
          onClick={() => handleClick(button)}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
          style={{
            padding: "10px 18px",
            background: "rgba(249, 250, 251, 0.6)",
            border: "1px solid rgba(0, 102, 204, 0.14)",
            borderRadius: "50px",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "13px",
            fontWeight: 400,
            color: button.textColor,
            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
            letterSpacing: "-0.1px",
            boxShadow:
              hoveredIndex === index
                ? "0 2px 6px rgba(0, 102, 204, 0.06)"
                : "0 1px 3px rgba(0, 0, 0, 0.04)",
            transform: hoveredIndex === index ? "translateY(-0.5px)" : "translateY(0)",
            transition: "all 200ms ease-out",
            outline: "none",
            whiteSpace: "nowrap",
            position: "relative",
            overflow: "hidden",
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = "translateY(0) scale(0.99)";
          }}
          onMouseUp={(e) => {
            if (hoveredIndex === index) {
              e.currentTarget.style.transform = "translateY(-0.5px)";
            }
          }}
        >
          {/* Animated gradient border layer */}
          <span
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: "50px",
              background: button.animatedGradient,
              backgroundSize: "300% 100%",
              opacity: hoveredIndex === index ? 1 : 0,
              filter: "blur(4px)",
              transition: "opacity 300ms ease-out",
              animation: hoveredIndex === index ? "gradientSpin 3s linear infinite" : "none",
              pointerEvents: "none",
            }}
          />

          {/* Inner background layer */}
          <span
            style={{
              position: "absolute",
              top: "2px",
              left: "2px",
              right: "2px",
              bottom: "2px",
              borderRadius: "50px",
              background: "#ffffff",
              pointerEvents: "none",
            }}
          />

          {/* Content layer */}
          <span
            style={{
              position: "relative",
              zIndex: 1,
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <button.Icon
              size={16}
              color={button.iconColor}
              style={{
                transform: hoveredIndex === index ? "rotate(12deg)" : "rotate(0deg)",
                transition: "transform 250ms ease",
              }}
            />
            <span>{button.title}</span>
          </span>
        </button>
      ))}

      <style>{`
        @keyframes gradientSpin {
          0% {
            background-position: 0% 50%;
          }
          100% {
            background-position: 300% 50%;
          }
        }
      `}</style>
    </div>
  );
};

export default QuickStartButtons;
